import {
  IncomeDetails,
  NITaxCategory,
  PensionContributionsType,
  PensionType,
  SupportedStudenLoanPlan,
  SupportedTaxYear,
} from "backend/src/types";

export const gbpFormatter = Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
}).format;

export const percFormatter = Intl.NumberFormat("en-GB", {
  style: "percent",
}).format;

export const getTitle = (input: IncomeDetails) => {
  let title = `${gbpFormatter(input.grossIncome / 100.0)}`;
  title += ` / N.I. Cat "${input.niTaxCategory || "A"}"`;
  if (input.pensionContributions > 0) {
    if (input.pensionType === "Salary Sacrifice") {
      title += " / SS Pension";
    } else {
      title += " / Pension";
    }
    if (input.pensionContributionsType === "Nominal") {
      title += ` ${gbpFormatter(input.pensionContributions / 100.0)}`;
    } else {
      title += ` ${percFormatter(input.pensionContributions)}`;
    }
  }
  if (input.isBlind) {
    title += ` / Blind`;
  }
  if (input.giftAid) {
    title += ` / Gift Aid ${gbpFormatter(input.giftAid)}`;
  }
  if (input.studentLoanPlans.length > 0) {
    title += ` / ${input.studentLoanPlans.join(" + ")}`;
  }
  if (input.taxCode) {
    title += ` / ${input.taxCode}`;
  }
  if (input.taxYear) {
    title += ` / Tax Year ${input.taxYear}`;
  }
  return title;
};

export const getSlug = (input: IncomeDetails): string => {
  let slug = `${input.grossIncome}${input.niTaxCategory}`;
  if (input.pensionContributions > 0) {
    const pensionAmount =
      input.pensionContributionsType === "Percentage"
        ? 100 * input.pensionContributions
        : input.pensionContributions;
    slug += `-${input.pensionContributionsType.charAt(
      0
    )}${pensionAmount}${input.pensionType.charAt(0).toUpperCase()}`;
  }
  if (input.studentLoanPlans.length > 0) {
    slug += `-SL${input.studentLoanPlans
      .map((name) => name.slice(-1))
      .sort()
      .join("")}`;
  }
  if (input.isBlind) {
    slug += `-BA`;
  }
  if (input.giftAid) {
    slug += `-GA${input.giftAid}`;
  }
  if (input.taxCode) {
    slug += `-C${input.taxCode}`;
  }
  if (input.taxYear) {
    slug += `-Y${input.taxYear}`;
  }
  return slug;
};

export const parseSlug = (slug: string): Partial<IncomeDetails> => {
  const incomeDetails: Partial<IncomeDetails> = {};
  const incomeAndNI = slug.match(/(\d+)([ABCFHIJLMSVZ]{1})/);
  const pension = slug.match(/-([NP]{1})(\d+)([AESP]{1})/);
  const studentLoans = slug.match(/-SL(1)?(2)?(4)?(5)?(n)?/);
  const isBlind = slug.match(/-BA/);
  const giftAid = slug.match(/-GA(\d+)/);
  const taxCode = slug.match(/-C(\d{1,4}[^\-]*)/);
  const taxYear = slug.match(/-Y(\d{2}-\d{2}(?:-[a-z]{3})?)/);
  const pensionContributionsTypeLookup: {
    [key: string]: PensionContributionsType;
  } = {
    N: "Nominal",
    P: "Percentage",
  };
  const pensionTypeLookup: { [key: string]: PensionType } = {
    A: "Auto Enrolment",
    E: "Employer",
    S: "Salary Sacrifice",
    P: "Personal",
  };
  const studentLoanLookup: { [key: string]: SupportedStudenLoanPlan } = {
    "1": "Plan 1",
    "2": "Plan 2",
    "4": "Plan 4",
    "5": "Plan 5",
    n: "Postgraduate Loan",
  };
  const taxYearLookup: { [key: string]: SupportedTaxYear } = {
    "20-21": "20-21",
    "21-22": "21-22",
    "22-23": "22-23",
    "22-23-jul": "22-23-jul",
    "22-23-nov": "22-23-nov",
    "23-24": "23-24",
    "23-24-jan": "23-24-jan",
  };
  if (incomeAndNI?.length === 3) {
    incomeDetails.grossIncome = parseInt(incomeAndNI[1]) / 100.0;
    incomeDetails.niTaxCategory = incomeAndNI[2] as NITaxCategory;
  }
  if (pension?.length === 4) {
    const pensionContributionsType = pensionContributionsTypeLookup[pension[1]];
    incomeDetails.pensionContributionsType = pensionContributionsType;
    if (incomeDetails.pensionContributionsType === "Nominal") {
      incomeDetails.pensionContributions = parseInt(pension[2]) / 100.0;
    } else {
      incomeDetails.pensionContributions = parseInt(pension[2]);
    }
    incomeDetails.pensionType = pensionTypeLookup[pension[3]];
  } else {
    incomeDetails.pensionContributionsType = "Percentage";
    incomeDetails.pensionContributions = 0;
    incomeDetails.pensionType = "Salary Sacrifice";
  }
  if (studentLoans?.length) {
    incomeDetails.studentLoanPlans = studentLoans
      ?.slice(1)
      .filter((value) => !!value)
      .map((studentLoanCode) => studentLoanLookup[studentLoanCode]);
  } else {
    incomeDetails.studentLoanPlans = [];
  }
  if (isBlind?.length) {
    incomeDetails.isBlind = true;
  } else {
    incomeDetails.isBlind = false;
  }
  if (giftAid?.length === 2) {
    incomeDetails.giftAid = parseInt(giftAid[1]) / 100.0;
  } else {
    incomeDetails.giftAid = 0;
  }
  if (taxCode?.length === 2) {
    incomeDetails.taxCode = taxCode[1];
  }
  if (taxYear?.length === 2) {
    incomeDetails.taxYear = taxYearLookup[taxYear[1]] || "23-24-jan";
  }
  return incomeDetails;
};
